// Crypto Wallets → Configuration → Hot Liquidity Policies (SM-279).
// Per asset: the HOT wallet's minimum (alert floor), target (refills and
// deposit-replenishment aim here) and optional maximum (retained-reserve
// ceiling — collection sweeps only the excess above it; empty = the hot
// wallet is never swept). One policy per asset; without one, HOT has no
// alert, no refills, and is never swept.
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { EmptyState } from "@/components/ui/EmptyState";
import { FormModal } from "@/components/ui/FormModal";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Skeleton } from "@/components/ui/Skeleton";
import { TokenAmountInput } from "@/components/ui/TokenAmountInput";
import { errMsg, td, th, useCanConfig } from "@/components/wallet-ops/configShared";
import {
  PolicyLoadError,
  PolicyPageHeader,
  RuleAmount,
} from "@/components/wallet-ops/policyShared";
import {
  fetchCryptoAssets,
  fetchLiquidityConfigs,
  putLiquidityConfig,
  walletKeys,
} from "@/lib/wallet-queries";
import type { CryptoAsset, CryptoLiquidityConfig } from "@/lib/wallet-types";
import { fmtRelative } from "@/lib/format";

export const Route = createFileRoute(
  "/_app/wallet-ops/config/policies/liquidity",
)({
  component: LiquidityPoliciesPage,
});

type EditorState =
  | { mode: "create" }
  | { mode: "edit"; cfg: CryptoLiquidityConfig }
  | null;

function LiquidityPoliciesPage() {
  const canConfig = useCanConfig();
  const assets = useQuery({
    queryKey: walletKeys.assets(),
    queryFn: () => fetchCryptoAssets(),
  });
  const configs = useQuery({
    queryKey: walletKeys.liquidityConfigs(),
    queryFn: fetchLiquidityConfigs,
  });
  const [editor, setEditor] = useState<EditorState>(null);

  const allAssets = assets.data ?? [];
  const rows = configs.data ?? [];
  const covered = new Set(rows.map((c) => c.asset_id));
  const assetByID = (id: string) => allAssets.find((a) => a.asset_id === id);
  const creatable = allAssets.filter((a) => !covered.has(a.asset_id));

  return (
    <div className="space-y-6">
      <PolicyPageHeader
        title="Hot liquidity policies"
        definition={
          <>
            The hot wallet is the float withdrawals are paid from. Its
            policy sets three levels: below the <b>minimum</b> an alert
            fires on the Hot Liquidity page; refills and deposit sweeps aim
            for the <b>target</b>; and collection may only sweep the excess
            above the optional <b>maximum</b> — with no maximum the hot
            wallet is never swept.
          </>
        }
        steps={[
          "balance below minimum? alert + request a refill",
          "refill/deposits replenish toward the target",
          "collection sweeps only the excess above the maximum",
        ]}
      />

      <Card>
        <CardHeader className="border-b border-border">
          <CardTitle>Policies</CardTitle>
          {canConfig && (
            <Button size="sm" onClick={() => setEditor({ mode: "create" })}>
              New policy
            </Button>
          )}
        </CardHeader>
        <CardBody className="p-0">
          {configs.isLoading ? (
            <div className="space-y-2 p-5">
              <Skeleton className="h-10" />
              <Skeleton className="h-10" />
            </div>
          ) : configs.isError ? (
            <PolicyLoadError
              message={errMsg(configs.error)}
              onRetry={() => void configs.refetch()}
            />
          ) : rows.length === 0 ? (
            <EmptyState
              className="m-5"
              title="No hot liquidity policies yet"
              description="Without a policy an asset's hot wallet has no low-liquidity alert, refills cannot be requested, and collection never touches it."
              action={
                canConfig && (
                  <Button size="sm" onClick={() => setEditor({ mode: "create" })}>
                    New policy
                  </Button>
                )
              }
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-surface-2">
                  <tr>
                    <th className={th}>Asset</th>
                    <th className={th}>Levels</th>
                    <th className={th}>Updated</th>
                    <th className={th} />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((c) => {
                    const asset = assetByID(c.asset_id);
                    const dec = asset?.token_decimals ?? 18;
                    return (
                      <tr
                        key={c.asset_id}
                        className="cursor-pointer border-b border-border/60 last:border-0 hover:bg-surface-2"
                        onClick={() => setEditor({ mode: "edit", cfg: c })}
                      >
                        <td className={td}>
                          <div className="font-medium text-t-1">
                            {asset?.symbol ?? c.asset_id}
                          </div>
                          <div className="font-mono text-2xs text-muted">
                            {c.asset_id}
                          </div>
                        </td>
                        <td className={td}>
                          <span className="text-t-2">
                            alert &lt;{" "}
                            <RuleAmount
                              base={c.min_base}
                              decimals={dec}
                              symbol={asset?.symbol}
                              none="0"
                            />{" "}
                            · target{" "}
                            <RuleAmount
                              base={c.target_base}
                              decimals={dec}
                              symbol={asset?.symbol}
                            />{" "}
                            ·{" "}
                            {c.max_base ? (
                              <>
                                sweep above{" "}
                                <RuleAmount
                                  base={c.max_base}
                                  decimals={dec}
                                  symbol={asset?.symbol}
                                />
                              </>
                            ) : (
                              "never swept"
                            )}
                          </span>
                        </td>
                        <td className={`${td} text-muted`}>
                          {c.updated_at ? fmtRelative(c.updated_at) : "—"}
                        </td>
                        <td className={`${td} text-right`}>
                          {canConfig && (
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditor({ mode: "edit", cfg: c });
                              }}
                            >
                              Edit
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      <LiquidityPolicyEditor
        state={editor}
        creatable={creatable}
        assetByID={assetByID}
        onClose={() => setEditor(null)}
      />
    </div>
  );
}

function LiquidityPolicyEditor({
  state,
  creatable,
  assetByID,
  onClose,
}: {
  state: EditorState;
  creatable: CryptoAsset[];
  assetByID: (id: string) => CryptoAsset | undefined;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const open = state !== null;
  const editing = state?.mode === "edit" ? state.cfg : null;
  const [draft, setDraft] = useState<CryptoLiquidityConfig>(blankConfig());
  const draftKey =
    state === null ? "closed" : state.mode === "create" ? "create" : state.cfg.asset_id;
  const [lastKey, setLastKey] = useState("closed");
  if (draftKey !== lastKey) {
    setLastKey(draftKey);
    setDraft(editing ? { ...editing } : blankConfig());
  }

  const save = useMutation({
    mutationFn: putLiquidityConfig,
    onSuccess: async () => {
      onClose();
      await qc.invalidateQueries({ queryKey: walletKeys.liquidityConfigs() });
      await qc.invalidateQueries({ queryKey: walletKeys.hotLiquidity() });
    },
    meta: {
      successMessage: editing
        ? "Hot liquidity policy updated."
        : "Hot liquidity policy created.",
    },
  });

  const set = <K extends keyof CryptoLiquidityConfig>(
    k: K,
    v: CryptoLiquidityConfig[K],
  ) => setDraft((d) => ({ ...d, [k]: v }));

  const asset = assetByID(draft.asset_id);
  const dec = asset?.token_decimals ?? 18;
  // min ≤ target ≤ max — mirror the server's validation so the modal
  // explains the problem before the 400 does.
  const min = BigInt(draft.min_base || "0");
  const target = draft.target_base === "" ? null : BigInt(draft.target_base);
  const max = draft.max_base === "" ? null : BigInt(draft.max_base);
  const orderError =
    target !== null && min > target
      ? "Minimum must not exceed the target."
      : target !== null && max !== null && target > max
        ? "Target must not exceed the maximum."
        : null;
  const canSubmit =
    draft.asset_id !== "" && draft.target_base !== "" && orderError === null;

  return (
    <FormModal
      open={open}
      title={
        editing
          ? `Edit hot liquidity policy — ${assetByID(editing.asset_id)?.symbol ?? editing.asset_id}`
          : "New hot liquidity policy"
      }
      description={
        editing
          ? "Changes apply from the next sweep pass and status refresh."
          : "One policy per asset — it drives the alert, refill defaults, and how collection treats the hot wallet."
      }
      confirmLabel={editing ? "Save changes" : "Create policy"}
      loading={save.isPending}
      error={save.isError ? errMsg(save.error) : null}
      canSubmit={canSubmit}
      onSubmit={() => save.mutate(draft)}
      onCancel={() => {
        onClose();
        save.reset();
      }}
    >
      <div className="space-y-4">
        {editing ? (
          <Input
            label="Asset"
            value={`${assetByID(editing.asset_id)?.symbol ?? editing.asset_id} (${editing.asset_id})`}
            disabled
            hint="A policy stays with its asset."
          />
        ) : (
          <Select
            label="Asset"
            placeholder="Pick an asset"
            value={draft.asset_id}
            onChange={(e) => set("asset_id", e.target.value)}
            options={creatable.map((a) => ({
              value: a.asset_id,
              label: `${a.symbol} · ${a.asset_id}`,
            }))}
            hint={
              creatable.length === 0
                ? "Every asset already has a policy — edit one from the list instead."
                : "Assets that already have a policy aren't listed."
            }
          />
        )}
        <TokenAmountInput
          label="Minimum (alert when below)"
          symbol={asset?.symbol}
          base={draft.min_base}
          decimals={dec}
          onBase={(b) => set("min_base", b)}
          allowEmpty
          emptyHint="empty = 0: the low-liquidity alert never fires."
        />
        <TokenAmountInput
          label="Target"
          symbol={asset?.symbol}
          base={draft.target_base}
          decimals={dec}
          onBase={(b) => set("target_base", b)}
          allowEmpty
          emptyHint="Required — refills default to target − balance, and deposit sweeps replenish the hot wallet up to it."
        />
        <TokenAmountInput
          label="Maximum (retained-reserve ceiling)"
          symbol={asset?.symbol}
          base={draft.max_base}
          decimals={dec}
          onBase={(b) => set("max_base", b)}
          allowEmpty
          emptyHint="empty = the hot wallet is never swept to the collection wallet."
        />
        {orderError && <p className="text-2xs text-danger">{orderError}</p>}
        <p className="text-2xs text-muted">
          Ordering rule: minimum ≤ target ≤ maximum. The band between
          target and maximum is a stable buffer — deposits stop topping up
          at the target, collection only trims above the maximum.
        </p>
      </div>
    </FormModal>
  );
}

function blankConfig(): CryptoLiquidityConfig {
  return { asset_id: "", min_base: "", target_base: "", max_base: "" };
}
